<template>
  <div>
    <app-detail-header :show-toggle="true"
      v-if="entity"
      :entity="entity"
      :show-new="false"
      :show-print="false"
      :show-delete="false"
      :show-save="true"
      @appDetailHeaderButtonClicked="onHeaderButtonClicked" />
    <div class="columns is-gapless">
      <div class="column is-narrow">
        <user-side-menu v-if="entity"
          :entity-name="`${entity.username}`"
          v-model="entity"
          ref="sideMenu"
          :is-mobile-app-user="isMobileAppUser" />
      </div>
      <div class="column">
        <router-view v-if="entity"
          v-model="entity"
          ref="currentChild"
          :is-tiled="false"
          :key="$route.fullPath"
          :route-name="$route.name"
          :is-allowed-access-new-pwa="isAllowedAccessNewPWA"
          @mobile-app-user-changed="isMobileUser = $event" />
      </div>
    </div>
    <unsaved-modal :active.sync="isUnsavedModalActive"
      @close="closeModal()"
      @skipSave="skipSave()"
      @saveContinue="saveContinue()">
      <p slot="text-title">Unsaved Changes</p>
      <p slot="text-content">There are unsaved changes. Please select action below</p>
    </unsaved-modal>
    <save-conflict-modal :active.sync="isSaveConflictModalActive"
      @close="closeModal()"
      @reload="reloadData()">
      <p slot="text-title">Change conflict</p>
      <p slot="text-content">The data on the server is newer than the local copy. Please reload local data.</p>
    </save-conflict-modal>
  </div>
</template>

<script>
import { AppHeaderButtonTypes, ModuleTypes } from '@/enums'
import AppDetailHeader from '../../components/AppDetailHeader/AppDetailHeader.vue'
import UserSideMenu from './UserSideMenu.vue'
import StoreMixin from './storeMixin.js'
import _cloneDeep from 'lodash.clonedeep'
import UserRoutes from './route-types'
import _isEmpty from 'lodash/isEmpty'
import UserValidation from './UserValidation'
import { CompanySettingService, UserService } from '../../services'
import HttpStatus from '../../components/http-status'
// import { EventHubTypes } from '../../enums'
import { UnsavedModal, SaveConflictModal } from '@/components/BulmaModal' // Import the modal
import { getErrorMessage } from '../auth/ErrorMessage'
import SuperadminUsersRoutes from '../../views/superadmin/user/route-types'

export default {
  name: 'UserView',
  components: {
    AppDetailHeader,
    UserSideMenu,
    UnsavedModal,
    SaveConflictModal
  },
  mixins: [StoreMixin, UserValidation],
  props: {
    returnUrl: String
  },
  data() {
    return {
      entity: null,
      toRoute: null,
      isUnsavedModalActive: false,
      isSaveConflictModalActive: false,
      isSkipSave: false,
      companyModules: [],
      isMobileUser: false
    }
  },
  computed: {
    validateError() {
      return (
        this.$v.$error ||
        this.$v.detailGroup.$error ||
        this.$v.contactsGroup.$error ||
        // || this.$v.emailsPhonesGroup.$error || this.$v.addressesGroup.$error || this.$v.contactsGroup.$error
        this.$v.profileNameGroup.$error ||
        this.$v.profileAddressesGroup.$error ||
        this.$v.profileEmailsGroup.$error ||
        this.$v.profilePhonesGroup.$error
      )
    },
    isAllowedAccessNewPWA() {
      return this.companyModules.some((module) => module.modDesc === ModuleTypes.FlexiquotePwaAccess.name && module.enabled)
    },
    isMobileAppUser() {
      return this.isMobileUser
    }
  },
  created() {
    this.getEntity()

    this.getCompanyModules()
  },
  mounted() {
    this.$v.$touch()
  },
  beforeDestroy() {},
  methods: {
    onHeaderButtonClicked(action) {
      switch (action) {
        case AppHeaderButtonTypes.Delete:
          break
        case AppHeaderButtonTypes.Save:
          this.save()
          break
        case AppHeaderButtonTypes.Cancel:
          this.cancel()
          break
      }
    },
    clearSessionStorage() {
      this.removeQueries()
    },
    removeQueries() {
      sessionStorage.removeItem(`${this.$userInfo.sessionId}|user|returnUrl`)
    },
    async getEntity(reset = false) {
      this.$showSpinner()
      try {
        if (reset) {
          this.clearSnapshots(this.$route.params.userId)
        }
        if (!this.currentSnapshot) {
          await this.getStoreItem(this.$route.params.userId)
        }
        this.entity = _cloneDeep(this.currentSnapshot)
      } catch (e) {
        this.$notification.openMessageXhrError('', e)
      }
      this.$hideSpinner()
    },
    cancel() {
      this.saveSnapshot(_cloneDeep(this.entity))
      if (this.returnRoute && !_isEmpty(this.returnRoute)) {
        this.$router.push({
          path: this.returnRoute.path,
          query: this.returnRoute.query
        })
      } else if (this.toRoute) {
        this.$router.push(this.toRoute.fullPath)
      } else {
        if (this.returnUrl) {
          if (this.snapshotDiff && !this.isSkipSave && !this.isSaveContinue) {
            this.isUnsavedModalActive = true
          } else {
            this.clearSessionStorage()
            this.clearSnapshots(this.entity.userId)
            this.$showSpinner('Returning...')
          }
        } else {
          if (this.entity.isNew && this.isSkipSave) {
            this.clearSnapshots(this.entity.userId)
          }
          if (this.$userInfo.isSystemAdministrator && !this.$userInfo.isSupportUser) {
            this.$router.push({
              name: SuperadminUsersRoutes.UserListView.name
            })
            return
          } else
            this.$router.push({
              name: UserRoutes.UserListView.name
            })
        }
      }
    },
    async save() {
      const message = 'Validation errors. Please fix before saving'
      const title = 'User'
      try {
        this.$showSpinner('Saving...')
        this.saveSnapshot(_cloneDeep(this.entity))

        this.$v.$touch()
        // this.$v.entity.profile.assetEmails.$touch()
        // this.$v.entity.profile.assetPhones.$touch()
        // this.$v.entity.profile.assetAddresses.$touch()

        if (!this.validateError) {
          var response
          if (this.entity.isNew) {
            response = await UserService.postUser(this.entity)
          } else if (this.snapshotDiff) {
            response = await UserService.putUser(_cloneDeep(this.entity), this.snapshotDiff)
          } else {
            this.$notification.openNotificationWithType('warning', title, 'No changes. Not saved')
            return false
          }
          if (this.isSaveContinue) {
            this.$notification.openNotificationWithType('success', title, 'Save successful')
          } else if (response.status === HttpStatus.NO_CONTENT) {
            await this.getEntity(true) // Refresh page data
            // this.dataChanged = false
            this.$refs.currentChild && this.$refs.currentChild.$emit('refreshProfile', this.entity)
            this.$notification.openNotificationWithType('success', title, 'Save successful')
          } else if (response.status === HttpStatus.CREATED) {
            // await this.getEntity(true)
            // this.$refs.currentChild && this.$refs.currentChild.$emit('refreshProfile', this.entity)
            this.entity.isNew = false
            this.clearSnapshots(this.entity.userId)
            this.saveSnapshot(_cloneDeep(this.entity))
            this.$notification.openNotificationWithType('success', title, 'New user added')
          }
          // this.$eventHub.$emit(EventHubTypes.EntitySaved)
          if (this.$userInfo.isSystemAdministrator && !this.$userInfo.isSupportUser) {
            this.$router.push({
              name: SuperadminUsersRoutes.UserListView.name
            })
            return
          } else
            this.$router.push({
              name: UserRoutes.UserListView.name
            })
          return true
        } else {
          this.$notification.openNotificationWithType('danger', title, message)
          return false
        }
      } catch (e) {
        console.log(e)
        if (e.response.request.status === HttpStatus.CONFLICT) {
          this.isSaveConflictModalActive = true
        } else if (e.response.request.status === HttpStatus.BAD_REQUEST) {
          var errormessage = getErrorMessage(e.response.data.message).message
          this.$notification.openNotificationWithType('danger', title, errormessage || message)
        } else {
          this.$notification.openMessageXhrError('', e)
        }
        return false
      } finally {
        this.$hideSpinner()
      }
    },
    closeModal() {
      this.isUnsavedModalActive = false
      this.isSaveConflictModalActive = false
      this.isSaveContinue = false
      this.toRoute = null
    },
    skipSave() {
      this.isUnsavedModalActive = false
      this.isSkipSave = true
      this.cancel()
    },
    async saveContinue() {
      this.isUnsavedModalActive = false
      this.isSaveContinue = true
      this.isSaveContinue = await this.save()
      if (this.isSaveContinue) {
        this.cancel()
      }
    },
    async reloadData() {
      this.isSaveConflictModalActive = false
      this.isSaveContinue = false
      this.getEntity(true)
    },
    async getCompanyModules() {
      try {
        const result = await CompanySettingService.getCompanyModules()
        this.companyModules = result || []
      } catch (e) {
        this.$notification.openMessageXhrError('', e)
        this.companyModules = []
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    this.saveSnapshot(_cloneDeep(this.entity))
    if (this.snapshotDiff && !this.isSkipSave && !this.isSaveContinue) {
      console.log(this.snapshotDiff)
      if (this.$route.path !== from.path) {
        this.$router.replace(from.path)
      }
      this.toRoute = to
      this.isUnsavedModalActive = true
    } else {
      this.clearSnapshots(this.entity.userId)
      this.clearSessionStorage()
      this.setReturnRoute({})
      next()
    }
  }
}
</script>